import { FCC } from "@/types/common";
import { TTalentForm } from "@/features/talent/types/talentForm";
import { Grid, Typography } from "@mui/material";
import { useEffect, useRef } from "react";
import { useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useGetTalentSchema } from "@/features/talent/hooks/useGetTalentSchema";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { Select } from "@/components/elements/Select";
import { InputDatePicker } from "@/components/elements/InputDatePicker";
import { dayjs } from "@/utils/dayjs";
import { RadioGroup } from "@/components/elements/RadioGroup";
import { useGetNationalityList, useGetResidenceStatusList } from "@/api/services/global/master";
import { useEnum } from "@/api/services/global/enum";
import { Form } from "@/components/elements/Form";
import { ERecruitmentStatus } from "@/types/enum";
import { useMe } from "@/api/services/main/auth";

export type TCreateTalentFromMyNumberCardTabForm = {
  defaultValues?: TTalentForm | null;
  onChange: (data: TTalentForm) => void;
  memo?: string | null;
  onValidate: (triggerFn: () => Promise<boolean>) => void;
};

export const CreateTalentFromMyNumberCardTabForm: FCC<TCreateTalentFromMyNumberCardTabForm> = ({ defaultValues, onChange, onValidate }) => {
  const { talentSchema } = useGetTalentSchema();
  const { nationalityOptions } = useGetNationalityList();
  const { residenceStatusOptions } = useGetResidenceStatusList();
  const { data: genderOptions } = useEnum("EGender");
  const { data: user } = useMe();
  const validationContextRef = useRef({ isFormSubmitted: false });

  const methods = useForm<TTalentForm>({
    defaultValues: { ...defaultValues },
    resolver: yupResolver(talentSchema),
    context: validationContextRef.current,
    mode: "all",
    reValidateMode: "onBlur",
  });
  const {
    control,
    setValue,
    getValues,
    trigger,
    watch,
    formState: { errors, isSubmitted, touchedFields },
  } = methods;

  const nickName = useWatch({ control, name: "nickName" });

  useEffect(() => {
    setValue("recruitmentStatus", ERecruitmentStatus.DRAFT);
    setValue("personInChargeId", Number(user?.data.id));
    if (!getValues("fullResidenceAddress")) {
      const combined = [getValues("prefecture"), getValues("city"), getValues("address")].filter(Boolean).join("");
      if (combined) setValue("fullResidenceAddress", combined);
    }
  }, []);

  useEffect(() => {
    onValidate(async () => {
      validationContextRef.current.isFormSubmitted = true;
      let isValid = false;
      await methods.handleSubmit(
        () => {
          isValid = true;
        },
        () => {
          isValid = false;
        },
      )();
      return isValid;
    });
  }, [methods, errors, onValidate]);

  useEffect(() => {
    if (isSubmitted || touchedFields.fullName || touchedFields.nickName) {
      trigger("fullName");
    }
  }, [nickName, trigger, isSubmitted, touchedFields.fullName, touchedFields.nickName]);

  useEffect(() => {
    const subscription = watch(() => onChange(getValues()));
    return () => subscription.unsubscribe();
  }, [watch, onChange, getValues]);

  const onSubmit = () => {};

  return (
    <Form methods={methods} onSubmit={onSubmit} schema={talentSchema} shouldBlock={false} enableBlocker={false}>
      <Typography variant="sub16Bold">特定在留カード情報</Typography>
      <Grid container spacing={3} mt={0}>
        <Grid item xs={6}>
          <FormField control={control} name="fullName" required={!nickName?.trim()} currentValueType="text">
            <Input />
          </FormField>
        </Grid>
        <Grid item xs={6}>
          <FormField control={control} name="gender" currentValueType="select" currentOptionsKey="gender">
            <RadioGroup direction="row" options={genderOptions} />
          </FormField>
        </Grid>

        <Grid item xs={12}>
          <FormField control={control} name="fullResidenceAddress" label="住所・住居地" currentValueType="text">
            <Input placeholder="入力してください" />
          </FormField>
        </Grid>

        <Grid item xs={12}>
          <FormField control={control} name="cardNumber" label="在留カード番号" currentValueType="text">
            <Input placeholder="入力してください" />
          </FormField>
        </Grid>

        <Grid item xs={6}>
          <FormField control={control} name="birthday" currentValueType="date">
            <InputDatePicker minDate={dayjs("1870/01/01")} maxDate={dayjs().subtract(18, "year")} />
          </FormField>
        </Grid>
        <Grid item xs={6}>
          <FormField control={control} name="periodOfStay" label="在留期限" currentValueType="date">
            <InputDatePicker />
          </FormField>
        </Grid>

        <Grid item xs={6}>
          <FormField control={control} name="nationalityId" currentValueType="select" currentOptionsKey="nationality">
            <Select placeholder="選択してください" options={nationalityOptions} />
          </FormField>
        </Grid>
        <Grid item xs={6}>
          <FormField control={control} name="residenceStatusId" currentValueType="select" currentOptionsKey="residenceStatus">
            <Select placeholder="選択してください" options={residenceStatusOptions} />
          </FormField>
        </Grid>
      </Grid>
    </Form>
  );
};
